import React, { useState, useEffect, useMemo } from "react";
import "./EditorPage.css";
import api from '../../api';
import TaskModal from './TaskModal';
import Tooltip from './Tooltip';

export default function EditorPage({ canEdit = false }) {
  const [selectedTask, setSelectedTask] = useState(null); // For modal


  // --- Submission Requests (now hooked to backend) ---
  const [submissionRequests, setSubmissionRequests] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]); // Available task types
  const [priorities, setPriorities] = useState([]); // Available priorities
  const [statuses, setStatuses] = useState([]); // Available statuses
  const [people, setPeople] = useState([]); // Available people/requesters
  const [teams, setTeams] = useState([]); // Available teams
  const [editingCell, setEditingCell] = useState(null); // Track which cell is being edited
  const [showAddForm, setShowAddForm] = useState(false); // Toggle for compact add form
  const [filters, setFilters] = useState({
    type: "",
    priority: "",
    status: "New", // Default to "New"
    requester: "",
  });
  const [newSubmission, setNewSubmission] = useState({
    title: "",
    type: "",
    priority: "",
    status: "",
    requester: "",
    email: "",
    description: "",
  });
  
  // Handle Enter key on title input to auto-submit
  const handleTitleKeyPress = async (e) => {
    if (e.key === 'Enter' && newSubmission.title.trim()) {
      e.preventDefault();
      e.stopPropagation();
      await addSubmissionRequest(e);
    }
  };
  const [showAddRequesterModal, setShowAddRequesterModal] = useState(false);
  const [newRequester, setNewRequester] = useState({ name: "", email: "" });
  const [requesterError, setRequesterError] = useState("");

// Fetch all data in parallel on mount for faster loading
useEffect(() => {
  Promise.all([
    api.get('/tasks/types'),
    api.get('/tasks/priorities'),
    api.get('/tasks/statuses'),
    api.get('/tasks/people'),
    api.get('/tasks/teams'),
    api.get('/tasks')
  ])
    .then(([typesRes, prioritiesRes, statusesRes, peopleRes, teamsRes, tasksRes]) => {
      // Set all dropdown options
      setTaskTypes(typesRes.data || []);
      setPriorities(prioritiesRes.data || []);
      setStatuses(statusesRes.data || []);
      setPeople(peopleRes.data || []);
      setTeams(teamsRes.data || []);
      // Set tasks/submissions
      setSubmissionRequests(Array.isArray(tasksRes.data) ? tasksRes.data : []);
    })
    .catch((err) => {
      console.error("Failed to fetch data:", err);
    });
}, []);

// Filter submission requests based on selected filters (memoized for performance)
const filteredSubmissionRequests = useMemo(() => {
  return submissionRequests.filter((r) => {
    if (filters.type && r.type !== filters.type) return false;
    if (filters.priority && r.priority !== filters.priority) return false;
    if (filters.status && r.status !== filters.status) return false;
    if (filters.requester && (r.personName || r.requester) !== filters.requester) return false;
    return true;
  });
}, [submissionRequests, filters]);





  // --- Submission Requests Handlers (Backend) ---
  const handleAddRequester = async (e) => {
    e.preventDefault();
    setRequesterError("");

    try {
      const res = await api.post('/tasks/people', {
        name: newRequester.name,
        emailAddress: newRequester.email
      });

      // Refresh people list
      const peopleRes = await api.get('/tasks/people');
      setPeople(peopleRes.data || []);

      // Set the new requester in the form
      setNewSubmission({
        ...newSubmission,
        requester: res.data.name,
        email: res.data.emailAddress
      });

      // Close modal and reset
      setShowAddRequesterModal(false);
      setNewRequester({ name: "", email: "" });
    } catch (err) {
      if (err.response?.status === 409) {
        setRequesterError("A requester with this email already exists");
      } else {
        setRequesterError(err.response?.data?.error || "Failed to create requester");
      }
    }
  };

  const addSubmissionRequest = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const trimmedTitle = newSubmission.title?.trim();
    if (!trimmedTitle) {
      console.log("❌ Cannot submit: title is empty");
      return; // Don't submit if title is empty
    }

    // Map frontend fields to backend field names
    // Only title is required, all others can be null
    const payload = {
      title: trimmedTitle,
      description: newSubmission.description || null,
      type: newSubmission.type || null,
      priority: newSubmission.priority || null,
      status: newSubmission.status || null,
      personName: newSubmission.requester || null,
      personEmail: newSubmission.email || null,
    };

    try {
      console.log("📤 Sending payload to /tasks:", payload);
      console.log("📤 API base URL:", api.defaults.baseURL);
      const res = await api.post('/tasks', payload);
      const saved = res.data;
      console.log("📦 Backend response:", saved);

      if (!res.status.toString().startsWith('2')) {
        console.error("Backend error:", saved);
        alert(saved.error || "Failed to save task");
        return;
      }

      setSubmissionRequests([...submissionRequests, saved]);
      // Reset only title, keep form open for quick additions
      setNewSubmission({
        ...newSubmission,
        title: "",
      });
    } catch (err) {
      console.error("Failed to add submission:", err);
      console.error("Error details:", err.response?.data || err.message);
      const errorMessage = err.response?.data?.error 
        || err.response?.data?.details 
        || err.message 
        || 'Failed to add submission. Please try again.';
      alert(errorMessage);
    }
  };



  const handleDeleteSubmission = async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      setSubmissionRequests(submissionRequests.filter((r) => r.id !== id));
    } catch (err) {
      console.error("Failed to delete submission:", err);
    }
  };

  const handleFieldUpdate = async (taskId, field, value) => {
    const task = submissionRequests.find(r => r.id === taskId);
    if (!task) return;

    // Optimistically update the UI immediately (don't await)
    let updatedTask = { ...task };
    
    if (field === 'requester') {
      updatedTask.personName = value || null;
      updatedTask.requester = value || null;
    } else if (field === 'team') {
      const selectedTeam = teams.find(t => t.name === value);
      updatedTask.teamName = value || null;
      updatedTask.team = value || null;
      updatedTask.team_id = selectedTeam?.id || null;
    } else {
      updatedTask[field] = value || null;
    }
    
    // Update state immediately for instant UI feedback
    setSubmissionRequests(prevRequests => prevRequests.map(r => 
      r.id === taskId ? updatedTask : r
    ));

    // Then update the backend asynchronously (fire and forget)
    (async () => {
      try {
        const selectedTeam = field === 'team' ? teams.find(t => t.name === value) : null;
        const selectedPerson = field === 'requester' ? people.find(p => p.name === value) : null;
        
        const payload = {
          title: task.title,
          description: task.description,
          type: field === 'type' ? (value || null) : task.type,
          priority: field === 'priority' ? (value || null) : task.priority,
          status: field === 'status' ? (value || null) : task.status,
          personName: field === 'requester' ? (value || null) : (task.personName || task.requester),
          personEmail: field === 'requester' ? (selectedPerson?.emailAddress || task.personEmail || task.email) : (task.personEmail || task.email),
          team_id: field === 'team' ? (selectedTeam?.id || null) : (task.team_id || null),
        };

        const res = await api.put(`/tasks/${taskId}`, payload);
        // Update with server response to ensure consistency
        setSubmissionRequests(prevRequests => prevRequests.map(r => 
          r.id === taskId ? res.data : r
        ));
      } catch (err) {
        console.error("Failed to update task:", err);
        // Revert optimistic update on error
        setSubmissionRequests(prevRequests => prevRequests.map(r => 
          r.id === taskId ? task : r
        ));
        alert('Failed to update task. Please try again.');
      }
    })();
  };


  return (
    <div className="calendar-admin">
      {/* --- Submission Requests Section (Now Backend Connected) --- */}
      <h2>Manage Submission Requests</h2>

      {/* Filter Section */}
      <div className="filter-section">
        <div className="filter-row">
          <div className="filter-field">
            <label>Type</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="filter-dropdown"
            >
              <option value="">All</option>
              {taskTypes.map((type) => (
                <option key={type.id} value={type.type}>
                  {type.type}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-field">
            <label>Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="filter-dropdown"
            >
              <option value="">All</option>
              {priorities.map((priority) => (
                <option key={priority.id} value={priority.priorityLevel}>
                  {priority.priorityLevel}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-field">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="filter-dropdown"
            >
              <option value="">All</option>
              {statuses.map((status) => (
                <option key={status.id} value={status.status}>
                  {status.status}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-field">
            <label>Requester</label>
            <select
              value={filters.requester}
              onChange={(e) => setFilters({ ...filters, requester: e.target.value })}
              className="filter-dropdown"
            >
              <option value="">All</option>
              {people.map((person) => (
                <option key={person.id} value={person.name}>
                  {person.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Compact Add Form */}
      <div className="add-submission-section">
        <div className="add-submission-buttons">
          <button 
            type="button" 
            className="toggle-add-form-btn"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? '−' : '+'} Add New Submission
          </button>
          <button 
            type="button" 
            className="add-requester-btn"
            onClick={() => setShowAddRequesterModal(true)}
            title="Add new requester"
          >
            + Requester
          </button>
        </div>
        
        {showAddForm && canEdit && (
          <form onSubmit={(e) => { e.preventDefault(); addSubmissionRequest(e); }} className="compact-add-form">
            <div className="compact-form-row">
              <input
                type="text"
                placeholder="Title *"
                value={newSubmission.title}
                onChange={(e) => setNewSubmission({ ...newSubmission, title: e.target.value })}
                onKeyPress={handleTitleKeyPress}
                required
                className="compact-input"
              />
              <select
                value={newSubmission.type || ""}
                onChange={(e) => setNewSubmission({ ...newSubmission, type: e.target.value || null })}
                className="compact-select"
              >
                <option value="">Type</option>
                {taskTypes.map((type) => (
                  <option key={type.id} value={type.type}>
                    {type.type}
                  </option>
                ))}
              </select>
              <select
                value={newSubmission.priority || ""}
                onChange={(e) => setNewSubmission({ ...newSubmission, priority: e.target.value || null })}
                className="compact-select"
              >
                <option value="">Priority</option>
                {priorities.map((priority) => (
                  <option key={priority.id} value={priority.priorityLevel}>
                    {priority.priorityLevel}
                  </option>
                ))}
              </select>
              <select
                value={newSubmission.status || ""}
                onChange={(e) => setNewSubmission({ ...newSubmission, status: e.target.value || null })}
                className="compact-select"
              >
                <option value="">Status</option>
                {statuses.map((status) => (
                  <option key={status.id} value={status.status}>
                    {status.status}
                  </option>
                ))}
              </select>
              <select
                value={newSubmission.requester || ""}
                onChange={(e) => {
                  const selectedPerson = people.find(p => p.name === e.target.value);
                  setNewSubmission({ 
                    ...newSubmission, 
                    requester: e.target.value || null,
                    email: selectedPerson?.emailAddress || null
                  });
                }}
                className="compact-select"
              >
                <option value="">Requester</option>
                {people.map((person) => (
                  <option key={person.id} value={person.name}>
                    {person.name}
                  </option>
                ))}
              </select>
              <button type="submit" className="compact-submit-btn">Add</button>
            </div>
          </form>
        )}

        {/* Add Requester Modal */}
        {showAddRequesterModal && (
          <div className="modal-overlay" onClick={() => setShowAddRequesterModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Add New Requester</h3>
                <button 
                  className="modal-close"
                  onClick={() => {
                    setShowAddRequesterModal(false);
                    setRequesterError("");
                    setNewRequester({ name: "", email: "" });
                  }}
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleAddRequester} className="requester-form">
                <div className="form-field">
                  <label htmlFor="requester-name">Name *</label>
                  <input
                    type="text"
                    id="requester-name"
                    value={newRequester.name}
                    onChange={(e) => {
                      setNewRequester({ ...newRequester, name: e.target.value });
                      setRequesterError("");
                    }}
                    required
                    className="modal-input"
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="requester-email">Email *</label>
                  <input
                    type="email"
                    id="requester-email"
                    value={newRequester.email}
                    onChange={(e) => {
                      setNewRequester({ ...newRequester, email: e.target.value });
                      setRequesterError("");
                    }}
                    required
                    className="modal-input"
                  />
                </div>
                {requesterError && (
                  <div className="error-message">{requesterError}</div>
                )}
                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={() => {
                      setShowAddRequesterModal(false);
                      setRequesterError("");
                      setNewRequester({ name: "", email: "" });
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn">Add Requester</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <table className="calendar-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Type</th>
            <th>Priority</th>
            <th>Status</th>
            <th>Requester</th>
            <th>Team</th>
          </tr>
        </thead>
        <tbody>
          {filteredSubmissionRequests.map((r) => (
            <tr 
              key={r.id}
              onClick={(e) => {
                // Don't open modal if clicking on a dropdown
                if (e.target.tagName !== 'SELECT') {
                  setSelectedTask(r);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <td>
                <Tooltip text={r.title} delay={500}>
                  <span className="task-title-cell">{r.title}</span>
                </Tooltip>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <select
                  value={r.type || ''}
                  onChange={(e) => handleFieldUpdate(r.id, 'type', e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="table-dropdown"
                  disabled={!canEdit}
                >
                  <option value="">-</option>
                  {taskTypes.map((type) => (
                    <option key={type.id} value={type.type}>
                      {type.type}
                    </option>
                  ))}
                </select>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <select
                  value={r.priority || ''}
                  onChange={(e) => handleFieldUpdate(r.id, 'priority', e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="table-dropdown"
                  disabled={!canEdit}
                >
                  <option value="">-</option>
                  {priorities.map((priority) => (
                    <option key={priority.id} value={priority.priorityLevel}>
                      {priority.priorityLevel}
                    </option>
                  ))}
                </select>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <select
                  value={r.status || ''}
                  onChange={(e) => handleFieldUpdate(r.id, 'status', e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="table-dropdown"
                  disabled={!canEdit}
                >
                  <option value="">-</option>
                  {statuses.map((status) => (
                    <option key={status.id} value={status.status}>
                      {status.status}
                    </option>
                  ))}
                </select>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <select
                  value={r.personName || r.requester || ''}
                  onChange={(e) => handleFieldUpdate(r.id, 'requester', e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="table-dropdown"
                  disabled={!canEdit}
                >
                  <option value="">-</option>
                  {people.map((person) => (
                    <option key={person.id} value={person.name}>
                      {person.name}
                    </option>
                  ))}
                </select>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <select
                  value={r.teamName || r.team || ''}
                  onChange={(e) => handleFieldUpdate(r.id, 'team', e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="table-dropdown"
                  disabled={!canEdit}
                >
                  <option value="">-</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.name}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {/* Task Modal */}
      {selectedTask && (
        <TaskModal 
          task={selectedTask} 
          onClose={() => setSelectedTask(null)}
          onUpdate={(updatedTask) => {
            setSubmissionRequests(submissionRequests.map(r => 
              r.id === updatedTask.id ? updatedTask : r
            ));
            setSelectedTask(updatedTask);
          }}
          onDelete={(taskId) => {
            setSubmissionRequests(submissionRequests.filter(r => r.id !== taskId));
            setSelectedTask(null);
          }}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
